import { NovelGallerySubReducer } from "@customer-gallery/redux/galleryTypedRedux";
import { useNovelCustomerSelector } from "@customer-gallery/redux/reduxHooks";
import {
    ICryptoAddressWithTokens,
    ICryptoAddressWithTokensAndWallets,
} from "@novel/shared/interfaces/CryptoAddress";
import { useMemo } from "react";
import {
    invertAddressesToWallets,
    uninvertAddressesToWallets,
    WalletWithAddresses,
} from "@novel/shared/utils/invertAddressToWallet";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";

export interface ICryptoAddressesReducerState {
    isLoading: boolean;
    cryptoAddresses: ICryptoAddressWithTokensAndWallets[];
}

const initialState: ICryptoAddressesReducerState = {
    isLoading: true,
    cryptoAddresses: [],
};

export const walletsReducer: NovelGallerySubReducer<ICryptoAddressesReducerState> = (
    state = initialState,
    action,
) => {
    switch (action.type) {
        case "LOADED_USER": {
            return {
                ...state,
                isLoading: false,

                ...(action.payload.user
                    ? {
                          cryptoAddresses: action.payload.user.cryptoAddresses || [],
                      }
                    : {}),
            };
        }

        case "LOG_OUT": {
            return {
                ...state,
                isLoading: false,
                cryptoAddresses: [],
            };
        }

        case "LOADING_USER":
        case "CONNECTING_WALLET":
        case "DISCONNECTING_WALLET": {
            return {
                ...state,
                isLoading: true,
            };
        }

        case "ERROR_LOADING_USER":
        case "ERROR_CONNECTING_WALLET":
        case "ERROR_DISCONNECTING_WALLET": {
            return {
                ...state,
                isLoading: false,
            };
        }

        case "CONNECTED_WALLET": {
            const { wallet } = action.payload;
            wallet.cryptoAddresses = wallet.cryptoAddresses.map((item) => ({
                ...item,
                cryptoAddressToTokens: item?.cryptoAddressToTokens || [],
            }));
            const presentStateWalletFirst = invertAddressesToWallets(state.cryptoAddresses);

            const withoutConnectedWallet: WalletWithAddresses<ICryptoAddressWithTokens>[] =
                presentStateWalletFirst.filter(({ cryptoWallet }) => cryptoWallet.id !== wallet.id);

            // Creating or overwriting the cryptoAddresses state based on the cryptoWallet ID
            const invertedStateWithNewWallet = uninvertAddressesToWallets(
                withoutConnectedWallet.concat({
                    cryptoWallet: wallet,
                    addresses: wallet.cryptoAddresses,
                }),
            );

            return {
                ...state,
                isLoading: false,
                cryptoAddresses: invertedStateWithNewWallet,
            };
        }

        case "DISCONNECTED_WALLET": {
            // Find the walletID in any of the addresses we have associated to the user and remove it from state.
            const addressWithWalletDisconnected = state.cryptoAddresses
                .map((address) => {
                    const updatedWallet = address.cryptoWallets.filter(
                        (wallet) => wallet.id !== action.payload.walletId,
                    );
                    return { ...address, cryptoWallets: updatedWallet };
                })
                .filter((address) => address.cryptoWallets.length);

            return {
                ...state,
                isLoading: false,
                cryptoAddresses: addressWithWalletDisconnected || [],
            };
        }

        case "LOADED_CLAIMING_TOKEN_LINE_ITEM": {
            return {
                ...state,
                isLoading: false,
                cryptoAddresses: state.cryptoAddresses.map((cryptoAddress) =>
                    cryptoAddress.id !== action.payload.cryptoAddress.id
                        ? cryptoAddress
                        : {
                              ...cryptoAddress,
                              ...action.payload.cryptoAddress,
                              cryptoWallets: cryptoAddress.cryptoWallets,
                          },
                ),
            };
        }

        case "TOGGLE_TOKEN_HIDDEN": {
            const updatedstate = state.cryptoAddresses.map((cryptoAddress) => {
                const addresses = cryptoAddress.cryptoAddressToTokens.map((ele) => {
                    if (ele.id === action.payload.id) {
                        return {
                            ...ele,
                            isHidden: action.payload.status ? action.payload.status : !ele.isHidden,
                        };
                    } else {
                        return ele;
                    }
                });
                return {
                    ...cryptoAddress,
                    cryptoAddressToTokens: addresses,
                };
            });

            return {
                ...state,
                isLoading: false,
                cryptoAddresses: updatedstate,
            };
        }

        default: {
            return state;
        }
    }
};

export function useWallets(options?: {
    evmOnly: boolean;
}): WalletWithAddresses<ICryptoAddressWithTokensAndWallets>[] {
    const cryptoAddresses = useNovelCustomerSelector((state) => state.addresses.cryptoAddresses);
    return useMemo(() => {
        const filteredAddresses = !options?.evmOnly
            ? cryptoAddresses
            : cryptoAddresses.filter((cryptoAddress) => cryptoAddress.blockchainType === "evm");

        return invertAddressesToWallets(filteredAddresses);
    }, [cryptoAddresses, options?.evmOnly]);
}

export function useOnlyWalletAndEvmAddress(): {
    cryptoWallet: ICryptoWallet;
    cryptoAddress: ICryptoAddressWithTokensAndWallets;
} | null {
    const wallets = useWallets({ evmOnly: true });

    const usersOnlyWalletAndEvmAddress: {
        cryptoWallet: ICryptoWallet;
        cryptoAddress: ICryptoAddressWithTokensAndWallets;
    } | null = useMemo(() => {
        return (
            (wallets.length === 1 &&
                wallets[0].addresses.length === 1 && {
                    cryptoAddress: wallets[0].addresses[0],
                    cryptoWallet: wallets[0].cryptoWallet,
                }) ||
            null
        );
    }, [wallets]);

    return usersOnlyWalletAndEvmAddress;
}
